import { createApp } from 'vue';
import { createBootstrap, useToast } from 'bootstrap-vue-next';
import * as BootstrapVueNext from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { setupAxiosInterceptors } from '@/shared/config/axios-interceptor';

import App from './app.vue';
import Multiselect from 'vue-multiselect';
import InfiniteLoading from 'v3-infinite-loading';
import router from './router';
import * as config from './shared/config/config';
import JhiItemCountComponent from './shared/jhi-item-count.vue';
import PageSizeSelect from './shared/components/page-size-select/page-size-select.vue';
import TooltipHelp from './shared/tooltip/tooltip-help.vue';
import KpiCard from './shared/components/kpi-card/kpi-card.vue';
import EntList from './shared/components/ent-list/ent-list.vue';
import EntColumn from './shared/components/ent-list/ent-column.vue';
import JhiSortIndicatorComponent from './shared/sort/jhi-sort-indicator.vue';
import HealthService from './admin/health/health.service';
import MetricsService from './admin/metrics/metrics.service';
import LogsService from './admin/logs/logs.service';
import JobsService from './admin/jobs/jobs.service';
import AlarmService from './admin/alarm/alarm.service';
import ConfigurationService from '@/admin/configuration/configuration.service';
import ActivateService from './account/activate/activate.service';
import UserManagementService from './admin/user-management/user-management.service';
import LoginService from './account/login.service';
import AccountService from './account/account.service';
import AlertService from './shared/alert/alert.service';
import SpaceService from '@/entities/space/space.service';

import 'bootstrap';
// CSS propre à bootstrap-vue-next : indispensable, ses composants (modale, dropdown, etc.) s'appuient sur des styles
// scoped qui y sont définis — ex. `.modal.show { display: block }`. Sans cet import, les modales restent invisibles
// (structure + classe `.show` présentes, mais jamais de `display:block`).
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
import './styles.scss';
import { formatDate, duration } from '@/shared/date/filters';
import TranslationService from '@/locale/translation.service';
import VueMatomo from 'vue-matomo';

const app = createApp(App);

config.initFortAwesome();
const i18n = config.initI18N();
const store = config.initVueXStore();

app.use(router);
app.use(store);
app.use(i18n);
app.use(createBootstrap());

// bootstrap-vue-next 0.24 n'enregistre pas les composants globalement via le plugin (conçu pour l'auto-import/tree-shaking).
// Les templates migrés utilisent les tags globaux `<b-navbar>`, `<b-modal>`, `<b-table>`, `<b-form-*>`… : on enregistre donc
// tous les composants exportés (préfixe `B*`) pour rétablir la résolution kebab-case (`<b-navbar>` → `BNavbar`).
Object.entries(BootstrapVueNext).forEach(([name, component]) => {
  if (/^B[A-Z]/.test(name)) {
    app.component(name, component as any);
  }
});

// Toast controller capturé une fois dans le contexte de l'app (injecté dans AlertService).
const toast = app.runWithContext(() => useToast());

// Composants globaux
app.component('font-awesome-icon', FontAwesomeIcon);
app.component('jhi-item-count', JhiItemCountComponent);
app.component('page-size-select', PageSizeSelect);
app.component('jhi-sort-indicator', JhiSortIndicatorComponent);
app.component('infinite-loading', InfiniteLoading);
app.component('multiselect', Multiselect);
app.component('tooltip-help', TooltipHelp);
app.component('kpi-card', KpiCard);
app.component('ent-list', EntList);
app.component('ent-column', EntColumn);

// Anciens filtres Vue 2 exposés en propriétés globales (usages `| formatDate` à corriger par module)
app.config.globalProperties.formatDate = formatDate;
app.config.globalProperties.duration = duration;

if (window.location.hostname === 'manager.trop-cash.com') {
  app.use(VueMatomo, {
    host: 'https://matomo.tropevent.com/',
    siteId: 6,
    router,
    disableCookies: false,
    requireConsent: false,
    excludedQueryParams: ['email', 'emailConfirmKey'],
  });
}

const translationService = new TranslationService(store, i18n);
const loginService = new LoginService();
const accountService = new AccountService(store, translationService, router);

// Services injectés (pattern factory conservé : `@Inject('x') x: () => XService` puis `this.x()`)
app.provide('loginService', () => loginService);
app.provide('activateService', () => new ActivateService());
app.provide('userManagementService', () => new UserManagementService());
app.provide('healthService', () => new HealthService());
app.provide('configurationService', () => new ConfigurationService());
app.provide('logsService', () => new LogsService());
app.provide('metricsService', () => new MetricsService());
app.provide('jobsService', () => new JobsService());
app.provide('alarmService', () => new AlarmService());
app.provide('translationService', () => translationService);
app.provide('accountService', () => accountService);
app.provide('alertService', () => new AlertService(toast));
app.provide('spaceService', () => new SpaceService());

router.beforeEach(async to => {
  // vue-router 4 : on retourne la cible (redirection) ou true/undefined (poursuite) au lieu d'appeler `next()`.
  // Mélanger un guard `async` (qui retourne une promesse) avec un `next()` différé dans un setTimeout est invalide
  // en vue-router 4 (« Invalid navigation guard ») et bloquait toutes les routes protégées par `authorities`.
  if (!to.matched.length) {
    return '/not-found';
  }
  if (to.meta && to.meta.authorities && (to.meta.authorities as string[]).length > 0) {
    // Sans ce court délai, le compte est considéré vide au démarrage → 403 injustifié.
    await new Promise(resolve => setTimeout(resolve, 1));
    const value = await accountService.hasAnyAuthorityAndCheckAuth(to.meta.authorities as string[]);
    if (!value) {
      sessionStorage.setItem('requested-url', to.fullPath);
      return '/forbidden';
    }
  }
  return true;
});

setupAxiosInterceptors(
  error => {
    const url = error.response?.config?.url;
    const status = error.status || error.response.status;
    if (status === 401) {
      // Store logged out state.
      store.commit('logout');
      if (!url.endsWith('api/account') && !url.endsWith('api/authenticate')) {
        // Ask for a new authentication
        loginService.openLogin();
        return;
      }
    }
    console.log('Unauthorized!');
    return Promise.reject(error);
  },
  error => {
    console.log('Server error!');
    return Promise.reject(error);
  },
);

app.mount('#app');
